import React, { useState, useMemo, useEffect } from 'react';
import { Menu, Space } from 'antd';

import { NavLink, useHistory, useLocation } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import propTypes from 'prop-types';
import { useAuth } from '../context/auth.context';
import { socketClient } from '../utility/SocketClient';
import { Badge } from 'antd';
import { useTheme } from 'styled-components';
import useApiFetch from '../hooks/useApiFetch';

const { SubMenu } = Menu;


function MenuItems({ darkMode, toggleCollapsedMobile, toggleCollapsed, topMenu, collapsed }) {
    const history = useHistory();
    const location = useLocation();
    const theme = useTheme();
    const mainPathSplit = window.location.pathname.split('/').filter((i) => !!i);
    const { data: allModules, loading: modulesLoading } = useApiFetch('/api/modules');

    const [openKeys, setOpenKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([window.location.pathname]);
    const [hasNewMessage, setHasNewMessage] = useState(false);

    useEffect(() => {
        const onChatMessage = (data) => {
            const messageData = typeof data === 'string' ? JSON.parse(data) : data;
            if (messageData.source === 'contact') {
                if (
                    window.location.pathname.startsWith('/admin/live-chat-support') ||
                    window.location.pathname.startsWith('/admin/inbox')
                ) {
                    return;
                }
                setHasNewMessage(true);
            }
        };
        socketClient.on('live_chat_message', onChatMessage);
        return () => socketClient.removeListener('live_chat_message', onChatMessage);
    }, []);

    const { currentUser, allowedResources } = useAuth();

    const sideBarData = useMemo(
        () => [
            // ── Top ────────────────────────────────────────────────
            {
                name: 'Getting Started',
                resourceName: 'default',
                icon: <FeatherIcon icon="star" fill="#FFCE00" style={{ color: '#FFCE00' }} />,
                path: '/admin/getting-started',
            },
            {
                name: 'Dashboard',
                resourceName: 'default',
                icon: <FeatherIcon icon="bar-chart-2" />,
                path: '/admin/overview',
            },

            // ── AI ASSISTANT ───────────────────────────────────────
            {
                type: 'group',
                label: 'AI ASSISTANT',
                items: [
                    {
                        name: 'Bot Setup',
                        subtitle: 'Profile · Business info',
                        icon: <FeatherIcon icon="zap" style={{ color: '#FF5A8E' }} />,
                        path: '/admin/content-management',
                        subMenu: [
                            { name: 'Profile', resourceName: 'gen_ai_queries', path: '/admin/bot-flow/profile', adminOnly: true },
                            { name: 'Business Information', resourceName: 'gen_ai_queries', path: '/admin/bot-flow/business-information', adminOnly: true },
                        ],
                    },
                    {
                        name: 'Training',
                        subtitle: 'Knowledge base',
                        resourceName: 'gen_ai_knowledge_base',
                        icon: <FeatherIcon icon="cpu" />,
                        path: '/admin/gen-ai/knowledge-base',
                    },
                    {
                        name: 'Forms & Queries',
                        subtitle: 'WA forms · User queries',
                        icon: <FeatherIcon icon="clipboard" />,
                        path: '/admin/wa-forms',
                        subMenu: [
                            { name: 'WA Forms', resourceName: 'wa_forms', path: '/admin/wa-forms' },
                            { name: 'User Queries', resourceName: 'gen_ai_queries', path: '/admin/gen-ai/queries' },
                        ],
                    },
                ],
            },

            // ── ENGAGEMENT ─────────────────────────────────────────
            {
                type: 'group',
                label: 'ENGAGEMENT',
                items: [
                    {
                        name: 'Bytes',
                        subtitle: 'Knowledge snippets',
                        resourceName: 'knowledge_bytes',
                        icon: <FeatherIcon icon="grid" />,
                        path: '/admin/bytes',
                    },
                    {
                        name: 'Events',
                        subtitle: 'Schedule & management',
                        resourceName: 'events',
                        icon: <FeatherIcon icon="calendar" />,
                        path: '/admin/event-management/events',
                    },
                    {
                        name: 'Contacts',
                        subtitle: 'Active · Import · Feedback',
                        icon: <FeatherIcon icon="book-open" />,
                        path: '/admin/contacts',
                        subMenu: [
                            { name: 'Active', resourceName: 'active_contacts', path: '/admin/contacts' },
                            { name: 'Import', resourceName: 'imported_contacts', path: '/admin/imported-contacts' },
                            { name: 'Feedbacks', resourceName: 'contact_feedback', path: '/admin/feedbacks' },
                        ],
                    },
                ],
            },

            // ── COMMUNICATION ──────────────────────────────────────
            {
                type: 'group',
                label: 'COMMUNICATION',
                items: [
                    {
                        name: 'Request Callback',
                        subtitle: 'Pool · History',
                        icon: <FeatherIcon icon="phone-forwarded" />,
                        path: '/admin/request-callback',
                        subMenu: [
                            { name: 'Request Pool', resourceName: 'request_callback', path: '/admin/request-callback/request-pool' },
                            { name: 'Replied By Me', resourceName: 'request_callback', path: '/admin/request-callback/replied-by-me' },
                            { name: 'Call History', resourceName: 'request_callback', path: '/admin/request-callback/call-history' },
                        ],
                    },
                    {
                        name: 'Tickets',
                        subtitle: 'Support tickets',
                        resourceName: 'ticket',
                        icon: <FeatherIcon icon="tag" />,
                        path: '/admin/tickets',
                    },
                    {
                        name: 'Inbox',
                        subtitle: 'Live support chat',
                        resourceName: 'live_support',
                        tag: hasNewMessage ? <Badge count="New" color="green" /> : null,
                        icon: <FeatherIcon icon="inbox" />,
                        path: '/admin/inbox',
                    },
                    {
                        name: 'Messaging',
                        subtitle: 'Email · WhatsApp',
                        icon: <FeatherIcon icon="mail" />,
                        path: '/admin/communications',
                        subMenu: [
                            { name: 'Send', resourceName: 'broadcast_communications', path: '/admin/communications' },
                            { name: 'Email Templates', resourceName: 'broadcast_communication_email_templates', path: '/admin/communications/email-templates' },
                            { name: 'WhatsApp Templates', resourceName: 'broadcast_communication_whatsapp_templates', path: '/admin/communications/whatsapp-templates' },
                        ],
                    },
                    {
                        name: 'Notification Templates',
                        subtitle: 'Emails your contacts receive',
                        icon: <FeatherIcon icon="file-text" />,
                        resourceName: 'notification_templates',
                        path: '/admin/notification-templates/contact',
                    },
                ],
            },

            // ── SETTINGS ───────────────────────────────────────────
            {
                type: 'group',
                label: 'SETTINGS',
                items: [
                    {
                        name: 'Users Management',
                        subtitle: 'Users · Departments',
                        icon: <FeatherIcon icon="users" />,
                        path: '/admin/users-management',
                        value: 'default',
                        subMenu: [
                            { name: 'Users', resourceName: 'users', path: '/admin/users-management/users' },
                            { name: 'Departments', resourceName: 'departments', path: '/admin/users-management/departments' },
                        ],
                    },
                    {
                        name: 'Web Chat Widget',
                        subtitle: 'Embed configuration',
                        resourceName: 'default',
                        adminOnly: true,
                        icon: <FeatherIcon icon="message-square" />,
                        path: '/admin/web-chat-widget',
                    },
                ],
            },
        ],
        [hasNewMessage],
    );

    // Flatten all modules (from groups and top-level) for path matching
    const allModulesList = useMemo(() => {
        const list = [];
        sideBarData.forEach((entry) => {
            if (entry.type === 'group') {
                entry.items.forEach((item) => list.push(item));
            } else {
                list.push(entry);
            }
        });
        return list;
    }, [sideBarData]);

    const findParentMenuPath = (currentPath) => {
        for (const module of allModulesList) {
            if (module.path === `/${mainPathSplit.slice(0, 2).join('/')}`) return `submenu-${module.path}`;
            if (module.subMenu) {
                const match = module.subMenu.find((s) => s.path === `/${mainPathSplit.join('/')}`);
                if (match) return `submenu-${module.path}`;
            }
        }
        return '/admin/overview';
    };

    useEffect(() => {
        setOpenKeys([findParentMenuPath(window.location.pathname)]);
    }, []);

    useEffect(() => {
        setSelectedKeys([window.location.pathname]);
    }, [location]);

    const onOpenChange = (keys) => {
        setOpenKeys([keys.length && keys[keys.length - 1]]);
    };

    const onClick = (item) => {
        setSelectedKeys([item.key]);
        if (item.keyPath.length === 1) {
            setOpenKeys([]);
        } else {
            setOpenKeys([item.keyPath[1]]);
        }
        if (item.key === '/admin/live-chat-support' || item.key === '/admin/inbox') {
            setHasNewMessage(false);
        }
    };

    const isAllowedItem = (item, allowedResourceNames) =>
        !item.adminOnly &&
        ((item.resourceName === 'default' && currentUser.role !== 'temp') ||
            allowedResourceNames.includes(item.resourceName));

    const filterModule = (module, allowedResourceNames) => {
        if (module.adminOnly) return null;
        if (isAllowedItem(module, allowedResourceNames)) {
            return module;
        }
        if (module.subMenu) {
            const filteredSub = module.subMenu.filter((s) => isAllowedItem(s, allowedResourceNames));
            if (filteredSub.length) return { ...module, subMenu: filteredSub };
        }
        return null;
    };

    const allowedRoutes = useMemo(() => {
        if (modulesLoading) return [];
        if (!allowedResources) return [];
        const allowedResourceNames = allowedResources.map((r) => r.name);

        if (currentUser.role === 'admin') return sideBarData;

        return sideBarData
            .map((entry) => {
                if (entry.type === 'group') {
                    const allowedItems = entry.items.map((item) => filterModule(item, allowedResourceNames)).filter(Boolean);
                    return allowedItems.length ? { ...entry, items: allowedItems } : null;
                }
                return filterModule(entry, allowedResourceNames);
            })
            .filter(Boolean);
    }, [allowedResources, hasNewMessage, allModules, sideBarData]);

    const renderModule = (module) => {
        if (module.subMenu) {
            return (
                <SubMenu
                    key={`submenu-${module.path}`}
                    icon={!topMenu && module.icon}
                    title={module.name}
                >
                    {module.subMenu.map((sub) => (
                        <Menu.Item
                            key={sub.path}
                            icon={sub.icon}
                            onClick={() => history.push(sub.path)}
                        >
                            <NavLink to={sub.path}>{sub.name}</NavLink>
                        </Menu.Item>
                    ))}
                </SubMenu>
            );
        }

        return (
            <Menu.Item
                key={module.path}
                icon={!topMenu && module.icon}
                title={module.name}
                onClick={() => history.push(module.path)}
            >
                <NavLink to={module.path}>
                    {module.tag ? (
                        <Space>
                            <span>{module.name}</span>
                            {module.tag}
                        </Space>
                    ) : (
                        module.name
                    )}
                </NavLink>
            </Menu.Item>
        );
    };

    return (
        <Menu
            onOpenChange={onOpenChange}
            onClick={onClick}
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={selectedKeys}
            openKeys={collapsed ? [] : openKeys}
            overflowedIndicator={<FeatherIcon icon="more-vertical" />}
        >
            {allowedRoutes.map((entry) => {
                if (entry.type === 'group') {
                    // collapsed: render items flat without group label
                    if (collapsed) {
                        return entry.items.map(renderModule);
                    }
                    return (
                        <Menu.ItemGroup
                            key={entry.label}
                            title={
                                <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', color: '#868EAE' }}>
                                    {entry.label}
                                </span>
                            }
                        >
                            {entry.items.map(renderModule)}
                        </Menu.ItemGroup>
                    );
                }
                return renderModule(entry);
            })}
        </Menu>
    );
}

MenuItems.propTypes = {
    darkMode: propTypes.bool,
    topMenu: propTypes.bool,
    toggleCollapsed: propTypes.func,
    events: propTypes.object,
};

export default MenuItems;
